import { Box, Divider, Tooltip, Typography } from "@material-ui/core";
import { PricingLotDetailsDialogStyles } from "./PricingLotDetailsDialogStyles";
import PricingCalculator from "features/pricingManagement/CreateActivityAndPricing/ActivityItemPricing/PricingCalculator/PricingCalculator";
import { useGetMaterialsPricesByActivityPriceIdQuery } from "features/pricing/pricingSlice";
import { useGetActivityItemMaterialsQuery } from "features/activity/activitySlice";
import { useGetVehicleTypesQuery } from "features/vehicles/vehicleSlice";
import { arrayToObject } from "utils/object-util";
import FormatService from "services/formatService";
import CustomInput from "components/CustomInput";
import LoadingSpinner from "components/LoadingSpinner";
import { ActivityItemPricingType, DIFFERENT_PREFERRED_VENDOR_TOOLTIP } from "features/pricingManagement/PricingManagementConsts";

const ComparisonList = ({ title, items, classes, showVariance = true }) => {
    const getMarginClassName = (variancePercentage) => {
        if (variancePercentage < 0) {
            return `${classes.comparisonMargin} ${classes.comparisonMarginNegative}`;
        }

        if (variancePercentage > 0) {
            return `${classes.comparisonMargin} ${classes.comparisonMarginPositive}`;
        }

        return classes.comparisonMargin;
    };

    const formatVariancePercentage = (variancePercentage) => {
        const formatted = FormatService.formatPercent(variancePercentage);

        if (variancePercentage > 0 && typeof formatted === "string" && !formatted.trim().startsWith("+")) {
            return `+${formatted}`;
        }

        return formatted;
    };

    return (
        <Box className={classes.comparisonSection}>
            <div className={classes.title}>{title}</div>
            <Box className={classes.comparisonListScroll}>
                <Box className={classes.comparisonList}>
                    {items.map((item, index) => (
                        <Tooltip title={item.tooltip || ""} arrow key={`${title}-${item.name}-${index}`}>
                            <Box className={classes.comparisonRow}>
                                <Typography className={classes.comparisonName}>
                                    {item.name}
                                    {item.vendorName && <span className={classes.comparisonVendorName}> ({item.vendorName})</span>}
                                </Typography>
                                <Box className={classes.comparisonValues}>
                                    <Typography className={classes.comparisonCharge}>{FormatService.formatCurrency(item.charge)}</Typography>
                                    {showVariance && <Typography className={getMarginClassName(item.variancePercentage)}>{formatVariancePercentage(item.variancePercentage)}</Typography>}
                                </Box>
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const getVehicleTypeLabel = (vehicleTypeIds, vehicleTypeLookup) => {
    let ids = null;
    try { ids = vehicleTypeIds ? JSON.parse(vehicleTypeIds) : null; } catch (_) { }
    if (!ids || ids.length === 0) return "All Vehicle Types";
    return ids.map((id) => vehicleTypeLookup[id]?.name || id).join(", ");
};

const PricingDetailsTab = ({ pricingData, pricingToEdit, setPricingToEdit, vendorLookup }) => {
    const classes = PricingLotDetailsDialogStyles();

    const { data: vehicleTypes, isLoading: isLoadingVehicleTypes } = useGetVehicleTypesQuery();
    const vehicleTypeLookup = arrayToObject(vehicleTypes || []);

    const isBasePlusMaterials = pricingData?.pricingType === ActivityItemPricingType.BASE_PLUS_MATERIALS;

    const { data: activityItemMaterials, isLoading: isLoadingMaterials } = useGetActivityItemMaterialsQuery(
        pricingData?.activityItemId,
        { skip: !pricingData?.activityItemId || !isBasePlusMaterials }
    );
    const { data: materialsPrices, isLoading: isLoadingMaterialsPrices } = useGetMaterialsPricesByActivityPriceIdQuery(
        pricingData?.activityItemPriceId,
        { skip: !pricingData?.activityItemPriceId || !isBasePlusMaterials }
    );

    const materialsPricesList = materialsPrices || [];
    const activityItemMaterialsList = activityItemMaterials || [];

    const materialsPricesLookup = arrayToObject(materialsPricesList, 'activityItemMaterialId');

    const availableMaterials = activityItemMaterialsList.map((material) => {
        const matchedPrice = materialsPricesLookup[material.id];
        return matchedPrice
            ? {
                ...material,
                ...matchedPrice,
                activityItemMaterialPriceId: matchedPrice.id
            }
            : material;
    });

    const calculatorValue = {
        ...pricingData,
        ...(pricingToEdit || {}),
        materialPrices: pricingToEdit?.materialPrices || [],
    };

    const handlePricingCalculatorChange = (val) => {
        setPricingToEdit((prev) => ({
            ...prev,
            price: val?.price,
            margin: val?.margin,
            charge: val?.charge,
            priceMode: val?.priceMode,
            surchargeAmount: val?.surchargeAmount,
            materialPrices: val?.materialPrices || [],
            priceConfig: val,
        }));
    };

    const handleBundlePriceChange = (val) => {
        const parsedValue = val === '' ? null : parseFloat(val) || 0;
        setPricingToEdit((prev) => ({
            ...prev,
            price: parsedValue,
            charge: parsedValue,
        }));
    };

    if (!pricingData) {
        return <Typography>No pricing details available.</Typography>;
    }

    // The two matrix endpoints name the dialog's vendor differently (vendorId vs preferredVendorId)
    const dialogVendorId = pricingData.vendorId ?? pricingData.preferredVendorId;

    return (
        <Box className={classes.tabContent}>
            <LoadingSpinner loading={isLoadingMaterials || isLoadingMaterialsPrices || isLoadingVehicleTypes} />
            <Box className={classes.detailsLayout}>
                <Box className={classes.leftColumn}>
                    <div className={classes.title}>Pricing Configuration</div>
                    {pricingData.vehicleTypeBreakdowns?.length >= 2 && (
                        <Typography className={classes.pricingForLabel}>
                            Price for: {getVehicleTypeLabel(pricingData.vehicleTypeIds, vehicleTypeLookup)}
                        </Typography>
                    )}
                    {pricingData.bundleId ?
                        <CustomInput
                            id="pricing-item-price"
                            label="Price"
                            value={pricingToEdit?.charge ?? pricingData.charge ?? 0}
                            onChange={handleBundlePriceChange}
                            elementType="input"
                            numberOnly
                            required
                        /> :
                        <PricingCalculator
                            key={pricingData?.id || 'empty'}
                            clientId={pricingData.clientId}
                            value={calculatorValue}
                            pricingType={pricingToEdit?.pricingType ?? pricingData.pricingType}
                            materials={availableMaterials}
                            onChange={handlePricingCalculatorChange}
                        />}
                </Box>
                <Box className={classes.centerDivider} />
                <Box className={classes.rightColumn}>
                    <Box className={classes.rightPanel}>
                        <ComparisonList
                            title="Same Client at Other Lots"
                            items={pricingData?.priceVarianceData?.sameClientOtherLots || []}
                            classes={classes}
                        />
                        <ComparisonList
                            title="Other Clients at Same Lot"
                            items={pricingData?.priceVarianceData?.otherClientsSameLot || []}
                            classes={classes}
                        />
                        {pricingData?.vehicleTypeBreakdowns?.length >= 2 && (
                            <>
                            <Divider />
                            <ComparisonList
                                title="Other Vehicle Type Prices"
                                items={pricingData.vehicleTypeBreakdowns.map((b) => {
                                    const isDifferentVendor = b.vendorId && dialogVendorId && b.vendorId !== dialogVendorId;
                                    const vendorName = isDifferentVendor ? (vendorLookup?.[b.vendorId]?.name || b.vendorId) : null;
                                    return {
                                        name: getVehicleTypeLabel(b.vehicleTypeIds, vehicleTypeLookup),
                                        charge: b.charge,
                                        vendorName,
                                        tooltip: vendorName ? DIFFERENT_PREFERRED_VENDOR_TOOLTIP(vendorName) : "",
                                    };
                                })}
                                classes={classes}
                                showVariance={false}
                            />
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default PricingDetailsTab;
